import { AlertTriangle, X } from '../../icons';
import { Button } from '../../components/shared';
import type { NodePublic, UsageReport } from '../../api/workspace';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tWorkspace } from '../../lib/workspace-i18n';
// 아이콘·이름 표는 workspaceUsageMeta 한 곳 (2026-08-19) — 모르는 종류도 받아 낸다.
import { usageLabelOf, usageMetaOf } from './workspaceUsageMeta';

/**
 * 삭제 영향 경고 (2026-08-14) — 사용자 질문에 대한 답을 화면으로.
 *
 *   "다른 곳에서 사용 중인데 삭제하면 어떤 문제가 발생할까?"
 *
 * 실제로 일어나는 일은 이렇다: 워크스페이스에서 지우면 그 파일을 첨부한 **채팅 메시지의
 * 첨부 카드는 그대로 남아 있는데 누르면 404** 가 된다(`presigned-download` 가
 * `deletedAt IS NULL` 을 요구한다). 조용히 깨지므로 **미리 말해 준다**.
 *
 * 막지는 않는다 — 휴지통으로 보내는 것은 복원이 가능하다. 되돌릴 수 없는 영구삭제는
 * 서버가 409 로 거절한다(두 정책이 다른 것은 의도다).
 */

export interface DeleteTarget {
  node: NodePublic;
  usage: UsageReport;
}

interface Props {
  /** 참조가 있는 대상들만 넘어온다(없으면 모달을 열지 않는다). */
  targets: DeleteTarget[];
  /** 참조가 없어 조용히 지워질 개수 — "3개 중 1개만 문제" 를 알려 준다. */
  cleanCount: number;
  onCancel: () => void;
  onConfirm: () => void;
}

export function WorkspaceDeleteWarnModal({ targets, cleanCount, onCancel, onConfirm }: Props) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  useEscapeClose(onCancel);

  return (
    <div
      className="workspace-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tWorkspace('workspace.delwarn.aria')}
    >
      <div ref={trapRef} className="workspace-modal ws-delwarn">
        <header className="workspace-modal-header">
          <h2 className="ws-delwarn-title">
            <AlertTriangle size={18} aria-hidden />
            {tWorkspace('workspace.delwarn.heading')}
          </h2>
          <Button variant="ghost" size="sm" iconOnly
            onClick={onCancel} aria-label={tWorkspace('workspace.modal.aria_close')}
            iconLeft={<X size={18} />}
          />
        </header>

        <div className="workspace-modal-body">
          <p className="ws-delwarn-lead">
            {tWorkspace('workspace.delwarn.lead', { count: targets.length })}
          </p>

          <ul className="ws-delwarn-list">
            {targets.map(({ node, usage }) => (
              <li key={node.id} className="ws-delwarn-item">
                <b className="ws-delwarn-name" title={node.name}>{node.name}</b>
                <ul className="ws-delwarn-refs">
                  {usage.refs.slice(0, 5).map((r) => {
                    const meta = usageMetaOf(r.kind);
                    const Icon = meta.icon;
                    return (
                      <li key={`${r.kind}-${r.id}`}>
                        <Icon size={13} aria-hidden />
                        <span className="ws-delwarn-kind">{usageLabelOf(r.kind)}</span>
                        <span className="ws-delwarn-label" title={r.label}>{r.label}</span>
                        {r.sublabel && <span className="ws-delwarn-sub">{r.sublabel}</span>}
                      </li>
                    );
                  })}
                  {usage.refs.length > 5 && (
                    <li className="ws-delwarn-more">
                      {tWorkspace('workspace.delwarn.more', { count: usage.refs.length - 5 })}
                    </li>
                  )}
                </ul>
              </li>
            ))}
          </ul>

          <p className="ws-delwarn-effect">{tWorkspace('workspace.delwarn.effect')}</p>

          {cleanCount > 0 && (
            <p className="ws-delwarn-clean">
              {tWorkspace('workspace.delwarn.clean', { count: cleanCount })}
            </p>
          )}

          <footer className="workspace-modal-footer">
            <Button variant="secondary"
              onClick={onCancel}
            >
              {tWorkspace('workspace.modal.btn_cancel')}
            </Button>
            <Button variant="danger"
              onClick={onConfirm}
            >
              {tWorkspace('workspace.delwarn.confirm')}
            </Button>
          </footer>
        </div>
      </div>
    </div>
  );
}
